import * as React from 'react';

import {
  Col,
  DataGrid,
  ListStatsCard,
  type ListStatsCardProps,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideArrowDown, LucideArrowUp, LucideScale } from 'lucide-react';

import { TagAllocationRecord } from '../../_graphql/queryInventoryTag';
import * as state from '../../_state/tagDetail.state';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';

import { useTagDetailAllocationsColumns } from './useTagDetailAllocationsColumns';

//
// ⚛️ ⚛️ ⚛️ TAG DETAIL OVERVIEW CONTENT ⚛️ ⚛️ ⚛️
//
export const TagDetailOverviewContent = () => {
  const t = useTranslations();

  // Atoms
  const { data, isFetching, loadingState } = useAtomValue(
    state.$remoteTagDetail,
  );
  const [allocationsLimit, setAllocationsLimit] = useAtom(
    state.$allocationsLimit,
  );
  const [allocationsOffset, setAllocationsOffset] = useAtom(
    state.$allocationsOffset,
  );

  // Other data hooks
  const { formatQuantityValue } = useInventoryUnit();
  const columns = useTagDetailAllocationsColumns();

  const tag = data ?? null;
  const allocations = tag?.allocations ?? [];
  const allocationsTotalCount = tag?.allocationsTotalCount ?? 0;
  const quantitySummary = tag?.quantitySummary;

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setAllocationsLimit(newLimit);
      setAllocationsOffset(newOffset);
    },
    [setAllocationsLimit, setAllocationsOffset],
  );

  // Memoized values
  const stats: ListStatsCardProps['stats'] = React.useMemo(() => {
    if (!tag) return [];
    const base: ListStatsCardProps['stats'] = [
      {
        key: 'netQuantity',
        label: t.tagsListView.detail.netQuantity,
        value: formatQuantityValue(tag.netQuantity, 'Pounds'),
        icon: <LucideScale />,
        color: 'primary',
      },
    ];
    if (quantitySummary) {
      base.push(
        {
          key: 'inQuantity',
          label: t.tagsListView.detail.inQuantity,
          value: formatQuantityValue(
            quantitySummary.inQuantity,
            quantitySummary.inQuantityUnit,
          ),
          icon: <LucideArrowDown />,
          color: 'success',
        },
        {
          key: 'outQuantity',
          label: t.tagsListView.detail.outQuantity,
          value: formatQuantityValue(
            quantitySummary.outQuantity,
            quantitySummary.outQuantityUnit,
          ),
          icon: <LucideArrowUp />,
          color: 'failure',
        },
        {
          key: 'netFromAllocations',
          label: t.tagsListView.detail.netFromAllocations,
          value: formatQuantityValue(
            quantitySummary.netQuantity,
            quantitySummary.netQuantityUnit,
          ),
          icon: <LucideScale />,
          color: 'primary',
        },
      );
    }
    return base;
  }, [tag, quantitySummary, t, formatQuantityValue]);

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.tagsListView.detail.noAllocations,
      description: '',
      centered: true,
    }),
    [t],
  );

  return (
    <Col className="@TagDetailOverviewContent p-5" gap5>
      {tag && stats.length > 0 && <ListStatsCard stats={stats} />}

      <DataGrid<TagAllocationRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        isContained
        dataSource={allocations}
        rowKey="id"
        aria-label={t.tagsListView.detail.table.aria}
        selectionMode="none"
        className="w-full text-sm"
        fullWidth
        emptyPanelProps={emptyPanelProps}
      />

      {allocationsTotalCount > allocationsLimit && (
        <div className="mt-4">
          <Pagination
            limit={allocationsLimit}
            offset={allocationsOffset}
            total={allocationsTotalCount}
            onChange={handlePaginationChange}
          />
        </div>
      )}
    </Col>
  );
};
